import { TkticSelect } from "@/components/ui/TkticSelect";
import type { Match } from "@/lib/types";
import { SLOT_LABELS, slotsForMatch } from "@/features/matches/matchSlots";

/**
 * Match + slot picker for Coach Review's VIDEO mode.
 *
 * A video coaching note is anchored to a specific match + slot
 * (+ timestamp). In M4 there's no HLS player yet, so the coach
 * picks the match and slot here; `timestamp_seconds` is sent as 0
 * ("start of the slot") until M5's player lets them scrub. The
 * backend's `CreateCoachingNoteRequest` per-context validator
 * requires `match_id` + `slot` + `timestamp_seconds` for video
 * notes — all three must be present, and `0` is a valid
 * timestamp (`ge=0`).
 *
 * Tactical-board mode does NOT render this — observations carry
 * no match anchor. The parent shows it only when `source ===
 * "video"`.
 *
 * The match list comes from `useMatches()` — TEAM-wide, across
 * every season (`getMatches()` suppresses `season_id`; see
 * tktic#8). A coach can attach a note to any of their team's
 * matches, not just the active season's. When the route already
 * carries `?match_id=`, the bundle resolved that match and the
 * parent seeds `matchId` here so the dropdown opens on it.
 */

export type MatchSlotPickerProps = {
  matches: Match[];
  matchesLoading: boolean;
  /** Selected match id, or "" when none picked yet. */
  matchId: string;
  onMatchIdChange: (id: string) => void;
  /** Selected slot — one of `_VALID_SLOTS`. */
  slot: string;
  onSlotChange: (slot: string) => void;
  /** When true, both selects are locked. Coach Review sets this in
   * clip-EDIT mode — a clip's match/slot anchor is immutable
   * (`UpdateCoachingClipRequest` is `extra="forbid"` and omits
   * them), so the picker must not let the coach change it. */
  disabled?: boolean;
};

/** Build the display label for a match. Exported so the clip
 * composer's read-only anchor banner uses the same format. */
export function matchLabel(m: Match): string {
  const teams = `${m.home_team} vs ${m.away_team}`;
  return m.date ? `${teams} · ${m.date}` : teams;
}

export function MatchSlotPicker({
  matches,
  matchesLoading,
  matchId,
  onMatchIdChange,
  slot,
  onSlotChange,
  disabled = false,
}: MatchSlotPickerProps) {
  // Slot options follow the selected match's video layout — a
  // `two_halves` match has no `full` video, so offering it would
  // 404 the player. Before a match is picked, fall back to the
  // full-match default.
  const selectedMatch = matches.find((m) => m.id === matchId);
  const slotOptions = slotsForMatch(selectedMatch).map((value) => ({
    value,
    label: SLOT_LABELS[value],
  }));
  return (
    <div
      className="flex items-center gap-2"
      data-testid="coach-review-match-slot-picker"
    >
      <label className="flex min-w-0 items-center gap-1.5">
        <span className="shrink-0 text-xs font-display uppercase tracking-wider text-ink-muted">
          Match
        </span>
        <TkticSelect
          value={matchId}
          onChange={onMatchIdChange}
          disabled={matchesLoading || disabled}
          data-testid="composer-match"
          placeholder={matchesLoading ? "Loading matches…" : "Select a match…"}
          className="w-56"
          options={[
            { value: "", label: matchesLoading ? "Loading matches…" : "Select a match…" },
            ...matches.map((m) => ({ value: m.id, label: matchLabel(m) })),
          ]}
        />
      </label>
      <label className="flex items-center gap-1.5">
        <span className="shrink-0 text-xs font-display uppercase tracking-wider text-ink-muted">
          Slot
        </span>
        <TkticSelect
          value={slot}
          onChange={onSlotChange}
          disabled={disabled}
          data-testid="composer-slot"
          className="w-32"
          options={slotOptions}
        />
      </label>
      {!matchesLoading && matches.length === 0 ? (
        <p className="whitespace-nowrap text-xs text-ink-muted">
          No matches in this team/season scope yet. Add a match in Admin first.
        </p>
      ) : null}
    </div>
  );
}
